/** Parse & helpers for `sensor_type_ids[]` on GET /api/stations (multi-sensor per station). */

export type StationSensorBindingDto = {
  id: string;
  sensor_type: string | null;
  sensor_type_name?: string | null;
  last_reading?: number;
  last_reading_at?: string;
  link?: Record<string, unknown> | null;
};

export const PRIMARY_ROUTING_ORDER = [
  'water_level',
  'rainfall',
  'soil_moisture',
  'temperature',
  'humidity',
] as const;

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

export function parseSensorTypeIdsFromDto(raw: Record<string, unknown>): StationSensorBindingDto[] {
  const arr = raw.sensor_type_ids;
  if (!Array.isArray(arr)) return [];
  const out: StationSensorBindingDto[] = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const id = typeof o.id === 'string' ? o.id.trim() : '';
    if (!id) continue;
    const sensor_type =
      typeof o.sensor_type === 'string' && o.sensor_type.trim() ? o.sensor_type.trim() : null;
    const binding: StationSensorBindingDto = { id, sensor_type };
    if (typeof o.sensor_type_name === 'string' && o.sensor_type_name.trim()) {
      binding.sensor_type_name = o.sensor_type_name.trim();
    }
    const lr = num(o.last_reading);
    if (lr !== undefined) binding.last_reading = lr;
    if (typeof o.last_reading_at === 'string' && o.last_reading_at.trim()) {
      binding.last_reading_at = o.last_reading_at.trim();
    }
    if (o.link !== undefined && o.link !== null && typeof o.link === 'object') {
      binding.link = o.link as Record<string, unknown>;
    }
    out.push(binding);
  }
  return out;
}

export function pickPrimaryBinding(
  bindings: StationSensorBindingDto[],
): StationSensorBindingDto | undefined {
  if (bindings.length === 0) return undefined;
  for (const rk of PRIMARY_ROUTING_ORDER) {
    const hit = bindings.find((b) => b.sensor_type === rk);
    if (hit) return hit;
  }
  return bindings[0];
}

export function buildReadingsByChannelId(
  bindings: StationSensorBindingDto[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const b of bindings) {
    if (b.last_reading !== undefined) out[b.id] = b.last_reading;
  }
  return out;
}

export function buildReadingsByRoutingKey(
  bindings: StationSensorBindingDto[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const b of bindings) {
    const rk = b.sensor_type?.trim();
    if (rk && b.last_reading !== undefined) out[rk] = b.last_reading;
  }
  return out;
}

export function routingKeyToChannelId(
  bindings: StationSensorBindingDto[],
  routingKey: string,
): string | undefined {
  const rk = routingKey.trim().toLowerCase();
  return bindings.find((b) => b.sensor_type?.trim().toLowerCase() === rk)?.id;
}

export function channelIdToRoutingKey(
  bindings: StationSensorBindingDto[],
  channelId: string,
): string | undefined {
  const id = channelId.trim();
  return bindings.find((b) => b.id === id)?.sensor_type?.trim() || undefined;
}

export function stationRoutingKeys(bindings: StationSensorBindingDto[]): string[] {
  return [
    ...new Set(
      bindings.map((b) => b.sensor_type?.trim()).filter((rk): rk is string => Boolean(rk)),
    ),
  ];
}

/** Map rainfall `duration_h` from WS → rule `window_code`. */
export function rainfallWindowCodeFromDurationH(durationH: number | undefined): '1h' | '24h' {
  if (durationH === 24) return '24h';
  return '1h';
}

export type StationSensorBindingInput = { id: string; link?: Record<string, unknown> };

export function bindingsToApiPayload(
  channelIds: string[],
  linksByChannelId?: Record<string, Record<string, unknown>>,
): StationSensorBindingInput[] {
  return channelIds.map((id) => ({
    id,
    ...(linksByChannelId?.[id] ? { link: linksByChannelId[id] } : {}),
  }));
}
